import { Link } from "react-router-dom";
import "../index.css";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { fetchFromAPI } from "../lib/api";

function getDaysLeft(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDate = new Date(dateStr);
  const diff = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
  return diff;
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return {
    day: d.toLocaleDateString("en-GB", { day: "2-digit" }),
    month: d.toLocaleDateString("en-GB", { month: "short" }).toUpperCase(),
    year: d.getFullYear(),
    full: d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
  };
}

const tagColors = {
  "Special Service": "bg-blue-50 text-blue-600 border-blue-200",
  "Weekly": "bg-gray-50 text-gray-500 border-gray-200",
  "Healing": "bg-green-50 text-green-600 border-green-200",
  "Convention": "bg-purple-50 text-purple-600 border-purple-200",
  "Celebration": "bg-amber-50 text-amber-600 border-amber-200",
};

const defaultImage = "https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2Fup.jpg?alt=media&token=823f7a7b-01e3-4dd6-8d56-d463fa0520d0";

const Upcoming = () => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        // Fetch all events without a limit parameter, so we can find the nearest active one
        const data = await fetchFromAPI("/events") || [];

        const formattedEvents = data.map((item, index) => {
          // support eventDate or fallback to older keys
          const dateObj = new Date(item.eventDate || item.date || item.datetime || item.dateTime || item.timestamp);
          let extractedTime = "TBD";
          let validDateStr = item.eventDate || item.date || item.datetime || item.dateTime;

          if (!isNaN(dateObj.getTime())) {
            extractedTime = dateObj.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
            validDateStr = dateObj.toISOString();
          }

          return {
            id: item.id || item._id || index,
            title: item.title,
            date: validDateStr,
            time: extractedTime,
            location: item.location,
            description: item.description || item.writeup || item.write_up || item.content || "",
            tag: item.tag || "Event",
            bannerImage: item.bannerImage || defaultImage
          };
        });

        // Filter out past events
        const todayAtMidnight = new Date();
        todayAtMidnight.setHours(0, 0, 0, 0);

        const upcomingAndTodayEvents = formattedEvents.filter(event => {
          const eventDateObj = new Date(event.date);
          return eventDateObj >= todayAtMidnight;
        });

        setEvents(upcomingAndTodayEvents);
      } catch (error) {
        console.error("Error formatting events:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const sorted = [...events].sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Find the first event that is today or in the future
  let featuredIndex = sorted.findIndex(e => new Date(e.date) >= today);
  
  // If all events are in the past, default to the most recent one (last in sorted array)
  if (featuredIndex === -1 && sorted.length > 0) {
    featuredIndex = sorted.length - 1; 
  }

  const featured = sorted[featuredIndex];
  
  // The rest of the events should not include the featured one
  const rest = sorted.filter((_, idx) => idx !== featuredIndex);

  let fd, featuredDays;
  if (featured) {
    fd = formatDate(featured.date);
    featuredDays = getDaysLeft(featured.date);
  }

  return (
    <motion.nav className="w-[100%]">

      {/* ── Hero ── */}
      <div className="relative w-full h-[40vh] md:h-[50vh] mt-32 md:mt-36 overflow-hidden">
        <img
          src={featured ? featured.bannerImage : defaultImage}
          alt={featured ? featured.title : "Upcoming Events"}
          className="w-full h-full object-cover object-center"
        />
      </div>

      <div className="max-w-5xl mx-auto px-5 md:px-10 py-16">

        {/* Section label */}
        <div className="flex items-center gap-2 mb-10">
          <span className="block w-7 h-px bg-blue-600" />
          <span className="text-blue-600 text-xs font-semibold tracking-widest uppercase">
            What's Coming
          </span>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            No upcoming events found.
          </div>
        ) : (
          <>
            {/* ── Featured / Next Event ── */}
            {featured && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col md:flex-row border border-blue-100 rounded-sm overflow-hidden mb-10"
              >
                {/* Date block */}
                <div className="bg-blue-600 text-white flex flex-col items-center justify-center px-10 py-8 flex-shrink-0 md:w-44">
                  <span className="text-5xl font-black leading-none">{fd.day}</span>
                  <span className="text-blue-200 text-sm font-semibold tracking-widest uppercase mt-1">{fd.month}</span>
                  <span className="text-blue-300 text-xs mt-0.5">{fd.year}</span>
                  <div className="w-8 h-px bg-blue-400 my-3" />
                  <span className="text-xs text-blue-200 font-semibold tracking-wide text-center">
                    {featuredDays > 0 ? `${featuredDays} days left` : featuredDays === 0 ? "Today!" : "Past"}
                  </span>
                </div>

                {/* Content */}
                <div className="p-7 flex flex-col justify-between gap-4 flex-1">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-[10px] font-bold tracking-widest uppercase border px-2 py-0.5 rounded-sm ${tagColors[featured.tag] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
                        {featured.tag}
                      </span>
                      <span className="text-[10px] text-gray-400 tracking-widest uppercase font-semibold">
                        {featuredDays >= 0 ? "Next Event" : "Recent Event"}
                      </span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 leading-snug mb-2">
                      {featured.title}
                    </h2>
                    <p className="text-gray-500 text-sm leading-relaxed">{featured.description}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-5 text-xs text-gray-400">
                    <span className="flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {featured.time}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {featured.location}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Rest of Events ── */}
            <div className="space-y-3">
              {rest.map((event, index) => {
                const d = formatDate(event.date);
                const daysLeft = getDaysLeft(event.date);

                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.07 }}
                    className="flex items-center gap-5 border border-gray-100 rounded-sm px-5 py-4 hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-200 group"
                  >
                    {/* Date pill */}
                    <div className="flex-shrink-0 text-center w-14">
                      <p className="text-2xl font-black text-gray-900 leading-none">{d.day}</p>
                      <p className="text-[10px] font-bold text-blue-600 tracking-widest uppercase">{d.month}</p>
                      <p className="text-[10px] text-gray-400">{d.year}</p>
                    </div>

                    {/* Divider */}
                    <div className="w-px h-10 bg-gray-200 flex-shrink-0" />

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-[9px] font-bold tracking-widest uppercase border px-1.5 py-0.5 rounded-sm ${tagColors[event.tag] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
                          {event.tag}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 truncate">{event.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-3">
                        <span>{event.time}</span>
                        <span className="hidden sm:inline">·</span>
                        <span className="hidden sm:inline truncate">{event.location}</span>
                      </p>
                    </div>

                    {/* Days left badge */}
                    <div className="flex-shrink-0 text-right">
                      <span className="text-[10px] font-bold text-gray-400 tracking-wide whitespace-nowrap">
                        {daysLeft > 0 ? `${daysLeft}d away` : daysLeft === 0 ? "Today" : "Past"}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}

        {/* Bottom note */}
        <div className="mt-12 border-t border-gray-100 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-gray-400 text-xs tracking-wide">
            All events are subject to change. Contact your local Bethel for confirmation.
          </p>
          <Link
            to="/Contact"
            className="flex items-center gap-2 border border-blue-600 text-blue-600 hover:bg-blue-50 text-xs font-semibold px-5 py-2.5 rounded-sm transition-all duration-200"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </motion.nav>
  );
};

export default Upcoming;